import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { ArrowLeft, Film, Trash2, Upload, Edit3, Clock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

export default function Drafts() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/drafts").then(r => setDrafts(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const publish = async (d) => {
    setErr(""); setBusy(b => ({ ...b, [d.id]: "publish" }));
    try {
      await api.post(`/drafts/${d.id}/publish`);
      setDrafts(arr => arr.filter(x => x.id !== d.id));
      setTimeout(() => nav("/"), 600); // open feed to see it
    } catch (e) {
      // If backend says the draft has no video, fall back to /upload?draft
      const detail = e?.response?.data?.detail || "";
      if (detail.toLowerCase().includes("no video")) {
        nav(`/upload?draft=${d.id}`);
        return;
      }
      setErr(formatErr(e));
    }
    finally { setBusy(b => ({ ...b, [d.id]: null })); }
  };

  const uploadOrPublish = (d) => {
    // Single smart CTA — publish if video is already uploaded, else open the
    // Upload screen with the draft pre-loaded so the user can pick a video.
    if (d.video_url) return publish(d);
    nav(`/upload?draft=${d.id}`);
  };

  const del = async (d) => {
    if (!window.confirm("Delete this draft? This cannot be undone.")) return;
    setBusy(b => ({ ...b, [d.id]: "del" }));
    try {
      await api.delete(`/drafts/${d.id}`);
      setDrafts(arr => arr.filter(x => x.id !== d.id));
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(b => ({ ...b, [d.id]: null })); }
  };

  const resume = (d) => nav(`/upload?draft=${d.id}`);

  return (
    <div className="px-4 py-6 pb-24" data-testid="drafts-page">
      <Link to="/more" className="flex items-center gap-1 text-xs text-white/60 mb-3" data-testid="drafts-back"><ArrowLeft className="w-3.5 h-3.5"/>Back</Link>

      <div className="flex items-center gap-2 mb-1">
        <Film className="w-7 h-7 text-[#FF7A00]"/>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Drafts</h1>
      </div>
      <p className="text-white/60 text-sm mb-4">Resume incomplete uploads or publish them when you're ready.</p>

      {err && <div className="mb-3 p-3 rounded-xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 text-[#FF2A5F] text-sm flex items-center gap-2" data-testid="drafts-err">
        <AlertTriangle className="w-4 h-4"/>{err}
      </div>}

      {loading ? (
        <div className="text-center text-white/50 py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2"/>Loading…</div>
      ) : drafts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center" data-testid="drafts-empty">
          <Film className="w-10 h-10 text-white/30 mx-auto mb-2"/>
          <p className="text-white/70 font-bold">No drafts yet</p>
          <p className="text-xs text-white/40 mt-1">If an upload fails mid-way, it will appear here automatically. You can also tap "Save as Draft" on the Upload screen.</p>
          <Link to="/upload" className="inline-block mt-4 px-5 py-2.5 rounded-full bg-[#00E5FF] text-black text-sm font-bold active:scale-95" data-testid="goto-upload">
            Start a new reel
          </Link>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="drafts-list">
          {drafts.map(d => (
            <div key={d.id} data-testid={`draft-${d.id}`}
              className="flex items-center gap-3 p-3 rounded-2xl border border-white/10 bg-white/5">
              <div className="w-16 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-white/10"
                style={d.thumbnail_url ? { backgroundImage: `url(${d.thumbnail_url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
                {!d.thumbnail_url && (
                  <div className="w-full h-full flex items-center justify-center">
                    {d.video_url ? <CheckCircle2 className="w-5 h-5 text-emerald-400"/> : <Clock className="w-5 h-5 text-[#FF7A00]"/>}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="text-sm font-bold text-white/95 truncate">{d.title || <span className="text-white/40 italic">Untitled draft</span>}</div>
                  <span className={`flex-shrink-0 px-1.5 py-0 rounded-full text-[9px] font-extrabold tracking-wider ${d.video_url ? "bg-emerald-500/20 text-emerald-300" : "bg-[#FF7A00]/15 text-[#FF7A00]"}`}>
                    {d.video_url ? "READY" : "INCOMPLETE"}
                  </span>
                </div>
                {d.description && <div className="text-[11px] text-white/60 truncate">{d.description}</div>}
                <div className="text-[10px] text-white/40 mt-0.5">Updated {new Date(d.updated_at).toLocaleString()}</div>
              </div>
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <button disabled={!!busy[d.id]} onClick={() => uploadOrPublish(d)}
                  data-testid={d.video_url ? `draft-publish-${d.id}` : `draft-upload-${d.id}`}
                  className={`px-3 py-1 rounded-full text-[11px] font-extrabold active:scale-95 flex items-center gap-1 disabled:opacity-50 ${d.video_url ? "bg-[#00E5FF] text-black" : "bg-[#FF7A00] text-black"}`}>
                  {busy[d.id] === "publish" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Upload className="w-3 h-3"/>}
                  {d.video_url ? "Publish" : "Upload"}
                </button>
                <button onClick={() => resume(d)} data-testid={`draft-resume-${d.id}`}
                  className="px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold active:scale-95 flex items-center gap-1">
                  <Edit3 className="w-3 h-3"/>Edit
                </button>
                <button disabled={!!busy[d.id]} onClick={() => del(d)} data-testid={`draft-del-${d.id}`}
                  className="px-3 py-1 rounded-full bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 text-[#FF2A5F] text-[11px] font-bold active:scale-95 flex items-center gap-1 disabled:opacity-50">
                  {busy[d.id] === "del" ? <Loader2 className="w-3 h-3 animate-spin"/> : <Trash2 className="w-3 h-3"/>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
